import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X, BookOpen, Maximize2, Minimize2, RotateCcw, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import HTMLFlipBook from 'react-pageflip';
import AudioPlayer from '../components/AudioPlayer';
import ShareButton from '../components/ShareButton';
import ReviewSection from '../components/ReviewSection';
import SleepTimer from '../components/SleepTimer';
import AmbientSounds from '../components/AmbientSounds';
import Paywall from '../components/Paywall';
import GardenMysteryBox from '../components/GardenMysteryBox';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useTheme } from '../context/ThemeContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useProfiles } from '../context/ProfileContext';
import { optimizedImageUrl } from '../utils/optimizedImage';
import VoiceReadButton from '../components/VoiceReadButton';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Split "Hoofdstuk 1: Titel\n\nBody..." into { chapter, body }
// Zo kunnen we het hoofdstuk-kopje apart renderen als in een echt boek.
const parseChapter = (text) => {
  if (!text) return { chapter: null, body: '' };
  const match = text.match(/^(Hoofdstuk\s+\d+\s*[:.]\s*[^\n]+)\n+([\s\S]*)$/);
  if (!match) return { chapter: null, body: text };
  return { chapter: match[1].trim(), body: match[2].trim() };
};

// Desktop Page component for the flip book
const Page = React.forwardRef(({ pageData, highlightedWord, storyId, isPremium, autoReadMode, onAudioEnded, children }, ref) => {
  const textRef = useRef(null);

  const renderTextWithHighlight = (text, highlightIndex) => {
    if (!text) return null;
    const words = text.split(/\s+/);
    return words.map((word, idx) => (
      <span
        key={idx}
        data-testid={`word-${idx}`}
        className={`inline-block px-1 py-0.5 rounded transition-all duration-200 ${
          idx === highlightIndex
            ? 'bg-gold/40 text-night-blue scale-110 font-semibold shadow-sm'
            : ''
        }`}
      >
        {word}{' '}
      </span>
    ));
  };

  const handleTouchMove = (e) => {
    const el = textRef.current;
    if (el && el.scrollHeight > el.clientHeight) {
      e.stopPropagation();
    }
  };

  return (
    <div ref={ref} className="bg-card p-4 md:p-6 h-full flex flex-col paper-texture overflow-hidden">
      {children || (
        <>
          {pageData?.image_url && (
            <div className="h-[45%] min-h-[120px] mb-3 rounded-xl overflow-hidden shadow-lg flex-shrink-0">
              <picture>
                <source srcSet={optimizedImageUrl(pageData.image_url, 'full')} type="image/webp" />
                <img
                  src={pageData.image_url}
                  alt={`Pagina ${pageData.page_number}`}
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-contain bg-muted/10"
                />
              </picture>
            </div>
          )}
          <div
            ref={textRef}
            onTouchMove={handleTouchMove}
            className={`flex-1 overflow-y-auto px-2 min-h-0 ${!pageData?.image_url ? 'flex flex-col items-center justify-center' : ''}`}
          >
            {(() => {
              const { chapter, body } = parseChapter(pageData?.text);
              return (
                <>
                  {chapter && (
                    <h2 className="font-display text-xl lg:text-2xl text-center text-gold mb-3 pb-2 border-b border-gold/20">
                      {chapter}
                    </h2>
                  )}
                  <p className={`leading-relaxed text-foreground font-body text-center py-1 ${!pageData?.image_url ? 'text-xl lg:text-2xl' : 'text-lg lg:text-xl'}`}>
                    {renderTextWithHighlight(body, highlightedWord)}
                  </p>
                </>
              );
            })()}
          </div>
          <div className="text-center text-muted-foreground text-xs mt-1 flex-shrink-0 flex items-center justify-center gap-2">
            <span>{pageData?.page_number}</span>
            {pageData?.text && (
              <VoiceReadButton
                text={pageData.text}
                storyId={storyId}
                pageNumber={pageData.page_number}
                isPremium={isPremium}
                autoPlay={autoReadMode}
                onEnded={onAudioEnded}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
});

Page.displayName = 'Page';

// Mobile page component — groot, leesbaar, full-width
const MobilePage = ({ pageData, highlightedWord, pageNumber, totalPages, storyId, isPremium, profileBadge, autoReadMode, onAudioEnded }) => {
  const renderTextWithHighlight = (text, highlightIndex) => {
    if (!text) return null;
    const words = text.split(/\s+/);
    return words.map((word, idx) => (
      <span
        key={idx}
        className={`inline-block px-0.5 py-0.5 rounded transition-all duration-200 ${
          idx === highlightIndex
            ? 'bg-gold/40 text-night-blue font-semibold'
            : ''
        }`}
      >
        {word}{' '}
      </span>
    ));
  };

  return (
    <div className="w-full flex flex-col" data-testid={`mobile-page-${pageNumber}`}>
      {/* Afbeelding — alleen als die er is */}
      {pageData?.image_url && (
        <div className="w-full rounded-2xl overflow-hidden shadow-lg mb-4" style={{ maxHeight: '45vh' }}>
          <picture>
            <source srcSet={optimizedImageUrl(pageData.image_url, 'full')} type="image/webp" />
            <img
              src={pageData.image_url}
              alt={`Pagina ${pageData.page_number}`}
              loading="eager"
              decoding="async"
              className="w-full h-full object-contain bg-muted/5"
            />
          </picture>
        </div>
      )}

      {/* Tekst — vult de ruimte als er geen afbeelding is */}
      <div className={`px-2 ${!pageData?.image_url ? 'flex-1 flex flex-col justify-center' : ''}`}>
        {(() => {
          const { chapter, body } = parseChapter(pageData?.text);
          return (
            <>
              {chapter && (
                <h2 className="font-display text-2xl text-center text-gold mb-4 pb-3 border-b border-gold/20">
                  {chapter}
                </h2>
              )}
              <p className={`leading-loose text-foreground font-body ${!pageData?.image_url ? 'text-2xl' : 'text-xl'}`}>
                {renderTextWithHighlight(body, highlightedWord)}
              </p>
            </>
          );
        })()}
      </div>

      {/* Paginanummer + voorlees-knop */}
      <div className="text-center text-muted-foreground text-sm mt-4 font-accent flex flex-wrap items-center justify-center gap-3">
        <span>{pageNumber} / {totalPages}</span>
        {pageData?.text && (
          <VoiceReadButton
            text={pageData.text}
            storyId={storyId}
            pageNumber={pageData?.page_number}
            isPremium={isPremium}
            autoPlay={autoReadMode}
            onEnded={onAudioEnded}
          />
        )}
        {profileBadge}
      </div>
    </div>
  );
};

const StoryReader = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { isBedtimeMode } = useTheme();
  const { canAccessStory, isPremium } = useSubscription();
  const { isAuthenticated, token, user } = useAuth();
  const { activeProfile } = useProfiles();
  const { t, lang } = useLanguage();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [highlightedWord, setHighlightedWord] = useState(-1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1100);
  const [mysteryBox, setMysteryBox] = useState({ open: false, rewards: [], new_badges: [], blocked_reason: null });
  // Auto-voorlees mode: na elke pagina-audio springt de reader vanzelf door
  // naar de volgende pagina en start daar opnieuw met voorlezen. Persistent
  // in localStorage zodat de keuze tussen verhalen behouden blijft.
  const [autoReadMode, setAutoReadMode] = useState(() => {
    try { return localStorage.getItem('autoReadMode') === '1'; } catch (e) { return false; }
  });
  const claimedRef = useRef(false);
  const readStartRef = useRef(Date.now());
  const pagesVisitedRef = useRef(new Set());
  const flipBookRef = useRef(null);
  const pageTurnSoundRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1100);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  useEffect(() => {
    fetchStory();
    pageTurnSoundRef.current = new Audio('https://cdn.pixabay.com/audio/2022/03/24/audio_7a3e6c7f15.mp3');
    pageTurnSoundRef.current.volume = 0.3;
  }, [storyId, lang]);

  useEffect(() => {
    if (story && isAuthenticated) {
      loadProgress();
    }
  }, [story, isAuthenticated]);

  const loadProgress = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/reading-progress/${storyId}`, { headers });
      const saved = res.data.current_page || 0;
      // Als het verhaal eerder helemaal is uitgelezen (saved >= aantal pagina's, dus op
      // de end-page of voorbij), start dan opnieuw vanaf de cover. Anders blijft de
      // gebruiker vastzitten op het "🌙 Mooi verhaal" scherm en kan hij niet herlezen.
      const total = (story?.pages?.length) || 0;
      const resumePage = saved > 0 && saved < total + 1 ? saved : 0;
      if (resumePage > 0) {
        if (isMobile) {
          setCurrentPage(resumePage);
        } else if (flipBookRef.current) {
          setTimeout(() => {
            flipBookRef.current.pageFlip().turnToPage(resumePage);
          }, 500);
        }
      }
    } catch (error) { /* progress load failed — niet kritisch */ }
  };

  // Herstart het verhaal vanaf de cover. Wordt aangeroepen via de "Lees opnieuw"-knop
  // op de end-page. Reset alleen de leesstaat — niet claimedRef, zodat de
  // mystery-box reward niet opnieuw kan worden gefarmed.
  const restartStory = useCallback(() => {
    readStartRef.current = Date.now();
    pagesVisitedRef.current = new Set();
    setHighlightedWord(-1);
    if (isMobile) {
      setCurrentPage(0);
      saveProgress(0);
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) {}
    } else if (flipBookRef.current) {
      try { flipBookRef.current.pageFlip().turnToPage(0); } catch (e) {}
      setCurrentPage(0);
      saveProgress(0);
    }
  }, [isMobile]);

  const saveProgress = async (page) => {
    if (!isAuthenticated || !story) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/reading-progress/${storyId}`, {
        current_page: page,
        total_pages: story.pages.length
      }, { headers });
    } catch (error) { /* progress save failed — niet kritisch */ }
  };

  const fetchStory = async () => {
    try {
      const response = await axios.get(`${API}/stories/${storyId}?lang=${lang}`);
      setStory(response.data);
      if (!response.data.is_free && !isPremium && !user?.is_admin) {
        setShowPaywall(true);
      }
      if (isAuthenticated && token) {
        axios.post(`${API}/reading-streak/update`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      }
    } catch (error) {
      console.error('Failed to fetch story:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const playPageTurnSound = () => {
    if (pageTurnSoundRef.current) {
      pageTurnSoundRef.current.currentTime = 0;
      pageTurnSoundRef.current.play().catch(() => {});
    }
  };

  const handlePageFlip = useCallback((e) => {
    setCurrentPage(e.data);
    playPageTurnSound();
    setHighlightedWord(-1);
    saveProgress(e.data);
  }, [isAuthenticated, story]);

  // Mobile navigation
  const goToMobilePage = (direction) => {
    const totalPages = story.pages.length + 1; // +1 for cover
    const newPage = currentPage + direction;
    if (newPage < 0 || newPage > totalPages) return;
    setCurrentPage(newPage);
    playPageTurnSound();
    setHighlightedWord(-1);
    saveProgress(newPage);
    // Scroll naar boven
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Subtiele haptic feedback (alleen op apparaten die het ondersteunen — iOS Safari soms,
  // Android Chrome bijna altijd). Faalt stil als de browser het niet kan.
  const haptic = () => {
    try {
      if (navigator.vibrate) navigator.vibrate(8);
    } catch (_) { /* no-op */ }
  };

  const goToNextPage = () => {
    haptic();
    if (isMobile) {
      goToMobilePage(1);
    } else if (flipBookRef.current) {
      flipBookRef.current.pageFlip().flipNext();
    }
  };

  const goToPrevPage = () => {
    haptic();
    if (isMobile) {
      goToMobilePage(-1);
    } else if (flipBookRef.current) {
      flipBookRef.current.pageFlip().flipPrev();
    }
  };

  // Wordt aangeroepen door VoiceReadButton zodra de audio van een pagina
  // helemaal is afgespeeld. In auto-voorlees mode bladeren we vanzelf door
  // naar de volgende pagina; de VoiceReadButton begint daar automatisch
  // opnieuw met voorlezen door de gewijzigde pageNumber-dep.
  const handleAudioEnded = useCallback(() => {
    if (!autoReadMode) return;
    const total = story?.pages?.length || 0;
    // Niet doorgaan voorbij de laatste leespagina; eindpagina zelf hoeft
    // niet voorgelezen te worden.
    if (currentPage >= total) {
      setAutoReadMode(false);
      try { localStorage.setItem('autoReadMode', '0'); } catch (e) {}
      return;
    }
    // Korte pauze zodat de stilte tussen pagina's natuurlijk aanvoelt.
    setTimeout(() => goToNextPage(), 350);
  }, [autoReadMode, story, currentPage, isMobile]);

  const toggleAutoRead = () => {
    setAutoReadMode((v) => {
      const next = !v;
      try { localStorage.setItem('autoReadMode', next ? '1' : '0'); } catch (e) {}
      if (next) {
        toast.success('Auto-voorlezen aan: na elke pagina ga je vanzelf door.');
      } else {
        toast.info('Auto-voorlezen uit.');
      }
      return next;
    });
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.().catch(() => {});
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const handleWordHighlight = (wordIndex) => {
    setHighlightedWord(wordIndex);
  };

  // Track pagina-views voor anti-doorklik beveiliging
  useEffect(() => {
    if (story && currentPage >= 1 && currentPage <= story.pages.length) {
      pagesVisitedRef.current.add(currentPage);
    }
  }, [currentPage, story]);

  // Preload de huidige + volgende afbeelding zodat pagina-omslagen instant voelen
  useEffect(() => {
    if (!story?.pages) return;
    const indices = [currentPage, currentPage + 1].filter((n) => n >= 1 && n <= story.pages.length);
    indices.forEach((n) => {
      const img = story.pages[n - 1]?.image_url;
      if (!img) return;
      const webp = new Image(); webp.src = optimizedImageUrl(img, 'full');
      const png  = new Image(); png.src = img;
    });
  }, [currentPage, story]);

  // Als de gebruiker de eindpagina bereikt → claim mystery box beloning
  useEffect(() => {
    if (!story || !isAuthenticated || !token) return;
    if (claimedRef.current) return;
    if (currentPage > story.pages.length) {
      claimedRef.current = true;
      (async () => {
        try {
          const readSec = (Date.now() - readStartRef.current) / 1000;
          const res = await axios.post(
            `${API}/garden/claim`,
            {
              story_id: storyId,
              read_seconds: readSec,
              pages_viewed: pagesVisitedRef.current.size,
              total_pages: story.pages.length,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setMysteryBox({
            open: true,
            rewards: res.data.rewards || [],
            new_badges: res.data.new_badges || [],
            blocked_reason: res.data.blocked_reason || null,
          });
        } catch (err) {
          // niet-kritiek: verhaal moet blijven werken
          console.error('Garden claim failed', err);
        }
      })();
    }
  }, [currentPage, story, isAuthenticated, token, storyId]);

  const closeMysteryBox = (navigateTo) => {
    setMysteryBox((m) => ({ ...m, open: false }));
    // Beveiliging: alleen daadwerkelijk navigeren wanneer navigateTo een string is.
    // (Voorkomt cyclic JSON error wanneer een React event als argument doorglipt.)
    if (typeof navigateTo === 'string' && navigateTo) navigate(navigateTo);
  };

  // Swipe support uitgeschakeld — touch was te gevoelig op tablets/mobiel.
  // Navigatie verloopt via duidelijke pijl-knoppen onderaan.
  const handleTouchStart = () => {};
  const handleTouchEnd = () => {};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="story-loading">
        <div className="text-center">
          <Skeleton className="w-80 h-96 rounded-xl mb-4" />
          <Skeleton className="w-48 h-6 mx-auto" />
        </div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="story-not-found">
        <div className="text-center">
          <BookOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="font-display text-2xl text-foreground">{t('reader.not_found')}</h2>
        </div>
      </div>
    );
  }

  const currentPageData = currentPage > 0 && currentPage <= story.pages.length
    ? story.pages[currentPage - 1]
    : null;
  const totalPagesWithCover = story.pages.length + 1;


  return (
    <div
      ref={containerRef}
      className={`min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4 flex flex-col items-center ${isFullscreen ? 'bg-background pt-4 md:pt-4' : ''}`}
      data-testid="story-reader"
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      <SEO path={`/story/${storyId}`} />
      {/* Paywall Modal */}
      <AnimatePresence>
        {showPaywall && (
          <Paywall
            story={story}
            onClose={() => {
              if (story.is_free || isPremium) {
                setShowPaywall(false);
              } else {
                navigate('/');
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Voortgangsbalk bovenaan */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-muted/30">
        <motion.div
          className="h-full bg-gold rounded-r-full"
          initial={{ width: '0%' }}
          animate={{ width: `${story.pages.length > 0 ? Math.round((currentPage / story.pages.length) * 100) : 0}%` }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        />
      </div>

      {/* Top buttons */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`fixed ${isFullscreen ? 'top-4' : 'top-4 md:top-24'} right-4 z-40 flex gap-2`}
      >
        <SleepTimer onTimerEnd={() => navigate('/')} />
        <AmbientSounds />
        {story && <ShareButton story={story} />}
        {(isPremium || user?.is_admin) && (
          <Button
            data-testid="toggle-auto-read"
            variant="ghost"
            size="icon"
            onClick={toggleAutoRead}
            title={autoReadMode ? 'Auto-voorlezen uit' : 'Auto-voorlezen aan'}
            aria-label="Auto-voorlezen"
            className={`rounded-full backdrop-blur-sm ${autoReadMode ? 'bg-gold/30 text-gold ring-2 ring-gold/40' : 'bg-card/80'}`}
          >
            {autoReadMode ? <PlayCircle className="w-5 h-5" /> : <PlayCircle className="w-5 h-5 opacity-60" />}
          </Button>
        )}
        <Button
          data-testid="toggle-fullscreen"
          variant="ghost"
          size="icon"
          onClick={toggleFullscreen}
          className="rounded-full bg-card/80 backdrop-blur-sm hidden md:flex"
        >
          {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
        </Button>
        <Button
          data-testid="close-story"
          variant="ghost"
          size="icon"
          onClick={() => navigate('/')}
          className="rounded-full bg-card/80 backdrop-blur-sm"
        >
          <X className="w-5 h-5" />
        </Button>
      </motion.div>

      {/* Story title */}
      <motion.h1
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="font-display text-2xl md:text-4xl text-foreground mb-4 md:mb-6 text-center mt-8 md:mt-0"
      >
        {story.title}
      </motion.h1>

      {/* === MOBILE READER === */}
      {isMobile ? (
        <div className="w-full max-w-lg">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.25 }}
              className="bg-card rounded-2xl p-5 shadow-xl border border-border min-h-[50vh]"
            >
              {/* Cover */}
              {currentPage === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-4">
                  {story.cover_image_url && (
                    <div className="w-full rounded-2xl overflow-hidden shadow-lg mb-5" style={{ maxHeight: '40vh' }}>
                      <img src={story.cover_image_url} alt={story.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <h2 className="font-display text-3xl text-foreground mb-2">{story.title}</h2>
                  {story.grandma_name && (
                    <p className="text-muted-foreground text-base font-accent">door {story.grandma_name}</p>
                  )}
                  {story.description && (
                    <p className="text-muted-foreground text-sm mt-3 leading-relaxed">{story.description}</p>
                  )}
                  <p className="text-gold text-sm mt-6 font-accent animate-pulse">Tik op het pijltje rechts om te beginnen →</p>
                </div>
              ) : currentPage <= story.pages.length ? (
                <MobilePage
                  pageData={story.pages[currentPage - 1]}
                  highlightedWord={highlightedWord}
                  pageNumber={currentPage}
                  totalPages={story.pages.length}
                  storyId={story.id}
                  isPremium={isPremium || user?.is_admin}
                  profileBadge={activeProfile && (
                    <span data-testid="story-for-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-old-rose/15 text-old-rose text-[10px] font-bold">
                      Voor <span style={{ color: activeProfile.color || '#db8f2d' }}>{activeProfile.name}</span> {activeProfile.avatar}
                    </span>
                  )}
                />
              ) : (
                /* End page */
                <div className="flex flex-col items-center justify-center text-center py-12">
                  <div className="text-6xl mb-4">🌙</div>
                  <h2 className="font-display text-3xl text-foreground mb-2">
                    {t('reader.end_title')}
                  </h2>
                  <p className="text-muted-foreground font-accent text-base mb-8">
                    {t('reader.end')}
                  </p>
                  <Button
                    data-testid="restart-story-btn"
                    onClick={restartStory}
                    className="bg-gold hover:bg-gold/90 text-night-blue font-bold rounded-full px-6 py-5 text-base shadow-lg"
                  >
                    <RotateCcw className="w-5 h-5 mr-2" /> Lees opnieuw
                  </Button>
                  <Button
                    data-testid="back-to-library-btn"
                    variant="ghost"
                    onClick={() => navigate('/library')}
                    className="mt-3 text-muted-foreground font-accent"
                  >
                    Terug naar bibliotheek
                  </Button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Mobile/tablet navigatie — duidelijke pijl-knoppen onderaan, geen onzichtbare tikzones meer */}
          <div className="fixed bottom-20 left-0 right-0 z-30 flex items-center justify-center gap-3 px-4">
            <Button
              data-testid="mob-prev-page"
              onClick={(e) => { e.stopPropagation(); goToPrevPage(); }}
              disabled={currentPage === 0}
              size="icon"
              className="h-14 w-14 rounded-full bg-card/95 backdrop-blur-md shadow-lg hover:bg-card disabled:opacity-30 text-foreground border border-border"
              aria-label="Vorige pagina"
            >
              <ChevronLeft className="w-7 h-7" />
            </Button>
            <div className="bg-card/90 backdrop-blur-md rounded-full px-4 py-2 shadow-md border border-border min-w-[72px] text-center">
              <span className="text-sm font-accent text-foreground font-medium">
                {currentPage} / {story.pages.length}
              </span>
            </div>
            <Button
              data-testid="mob-next-page"
              onClick={(e) => { e.stopPropagation(); goToNextPage(); }}
              disabled={currentPage > story.pages.length}
              size="icon"
              className="h-14 w-14 rounded-full bg-old-rose hover:bg-old-rose/90 text-night-blue shadow-lg disabled:opacity-30 border border-border"
              aria-label="Volgende pagina"
            >
              <ChevronRight className="w-7 h-7" />
            </Button>
          </div>
        </div>
      ) : (
        /* === DESKTOP FLIPBOOK === */
        <div className="relative w-full max-w-4xl">
          <Button
            data-testid="prev-page"
            variant="ghost"
            size="icon"
            onClick={goToPrevPage}
            disabled={currentPage === 0}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-30 rounded-full bg-card/80 backdrop-blur-sm -translate-x-12"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>

          <Button
            data-testid="next-page"
            variant="ghost"
            size="icon"
            onClick={goToNextPage}
            disabled={currentPage >= story.pages.length}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-30 rounded-full bg-card/80 backdrop-blur-sm translate-x-12"
          >
            <ChevronRight className="w-6 h-6" />
          </Button>

          <div className="shadow-2xl rounded-xl overflow-hidden">
            <HTMLFlipBook
              ref={flipBookRef}
              width={350}
              height={500}
              size="stretch"
              minWidth={280}
              maxWidth={600}
              minHeight={400}
              maxHeight={800}
              showCover={true}
              mobileScrollSupport={true}
              onFlip={handlePageFlip}
              className="mx-auto"
              style={{}}
              startPage={0}
              drawShadow={true}
              flippingTime={800}
              usePortrait={true}
              startZIndex={0}
              autoSize={true}
              maxShadowOpacity={0.5}
              showPageCorners={true}
              disableFlipByClick={false}
            >
              {/* Cover page */}
              <Page>
                <div className="flex flex-col items-center justify-center h-full text-center bg-gradient-to-b from-gold/10 to-old-rose/10 rounded-xl p-6">
                  {story.cover_image_url && (
                    <div className="w-full h-[50%] mb-4 rounded-xl overflow-hidden shadow-lg">
                      <img src={story.cover_image_url} alt={story.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <h2 className="font-display text-2xl md:text-3xl text-foreground mb-2">{story.title}</h2>
                  {story.grandma_name && (
                    <p className="text-muted-foreground text-sm font-accent">door {story.grandma_name}</p>
                  )}
                  {story.description && (
                    <p className="text-muted-foreground text-xs mt-3 leading-relaxed max-w-[80%]">{story.description}</p>
                  )}
                </div>
              </Page>
              {story.pages.map((page, index) => (
                <Page
                  key={page.page_number}
                  pageData={page}
                  highlightedWord={currentPage === index + 1 ? highlightedWord : -1}
                  storyId={story.id}
                  isPremium={isPremium || user?.is_admin}
                  autoReadMode={autoReadMode && currentPage === index + 1}
                  onAudioEnded={handleAudioEnded}
                />
              ))}
              {/* End page */}
              <Page>
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <div className="text-6xl mb-4">🌙</div>
                  <h2 className="font-display text-3xl text-foreground mb-2">
                    {t('reader.end_title')}
                  </h2>
                  <p className="text-muted-foreground font-accent mb-6">
                    {t('reader.end')}
                  </p>
                  <Button
                    data-testid="restart-story-btn-desktop"
                    onClick={restartStory}
                    className="bg-gold hover:bg-gold/90 text-night-blue font-bold rounded-full px-5 py-4 text-sm shadow-lg"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" /> Lees opnieuw
                  </Button>
                </div>
              </Page>
            </HTMLFlipBook>
          </div>

          {/* Page indicator desktop */}
          <div className="flex items-center justify-center gap-1.5 mt-4">
            {[...story.pages, null].map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentPage ? 'bg-gold w-4' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Audio player */}
      {currentPageData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 w-full max-w-md"
        >
          <AudioPlayer
            text={currentPageData.text}
            audioUrl={currentPageData.audio_url}
            onWordHighlight={handleWordHighlight}
            voice={isBedtimeMode ? "echo" : "nova"}
          />
        </motion.div>
      )}

      {/* Reviews */}
      <ReviewSection storyId={storyId} />

      <GardenMysteryBox
        open={mysteryBox.open}
        rewards={mysteryBox.rewards}
        newBadges={mysteryBox.new_badges}
        blockedReason={mysteryBox.blocked_reason}
        onClose={closeMysteryBox}
      />

      {/* Welterusten-overlay: verschijnt als de Mystery Box is gesloten in bedtime mode */}
      <GoodnightOverlay
        visible={isBedtimeMode && currentPage > story.pages.length && !mysteryBox.open}
        onClose={() => navigate('/library')}
      />
    </div>
  );
};

// === Welterusten-overlay (alleen in Bedtime Mode getoond na een uitgelezen verhaal) ===
const GoodnightOverlay = ({ visible, onClose }) => {
  const [show, setShow] = React.useState(false);
  // Precompute sterren positie/snelheid één keer (anders flikkeren ze bij elke re-render)
  const stars = React.useMemo(() => Array.from({ length: 30 }).map(() => ({
    left:  `${Math.random() * 100}%`,
    top:   `${Math.random() * 100}%`,
    size:  1 + Math.random() * 2,
    dur:   2 + Math.random() * 3,
    delay: Math.random() * 3,
  })), []);
  React.useEffect(() => {
    if (!visible) {
      setShow(false);
      return undefined;
    }
    const t = setTimeout(() => setShow(true), 400);
    return () => clearTimeout(t);
  }, [visible]);
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="goodnight"
          className="fixed inset-0 z-[180] flex items-center justify-center"
          style={{ background: 'radial-gradient(ellipse at center, hsl(28,40%,12%) 0%, hsl(28,35%,7%) 70%, hsl(28,35%,5%) 100%)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5 }}
          onClick={onClose}
          data-testid="goodnight-overlay"
        >
          <div className="absolute inset-0 pointer-events-none">
            {stars.map((s, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-amber-200"
                style={{ left: s.left, top: s.top, width: `${s.size}px`, height: `${s.size}px` }}
                animate={{ opacity: [0.2, 1, 0.2] }}
                transition={{ duration: s.dur, repeat: Infinity, delay: s.delay }}
              />
            ))}
          </div>
          <motion.div
            className="text-center px-8 z-10"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 1.2 }}
          >
            <div className="text-6xl mb-6">🌙</div>
            <h2 className="font-display text-4xl md:text-5xl text-amber-100 mb-3 tracking-wide">Welterusten</h2>
            <p className="text-amber-200/70 font-accent text-base md:text-lg">Slaap zacht, en droom mooi ✨</p>
            <p className="text-amber-300/40 text-xs mt-12 font-accent">tik om verder te gaan</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StoryReader;
